<script setup lang="ts">
/**
 * Google トレンド 検索インタレスト（DEV-14892）
 *
 * 「評価・エンゲージメント」カード内に配置し、movieId の変化で自前取得する。
 * 公開前モードには期間選択が無いため期間には依存せず、後端が返す「最新スナップショット」
 * （公開日基準の回溯ウィンドウ全体）をそのまま描画する。公開後モードの選択期間は背景強調のみ。
 *
 * 表示ルール（受入条件）：
 *  - 値は期間内ピーク=100 の相対値であり、実際の検索回数として表示しない
 *  - データなし・取得失敗を 0 で埋めない（null 日は線を繋がず、グレー背景＋ツールチップで「データ不足」と明示）
 *  - 前後がデータ不足の単日データは線分にならず消えるため、点で表示する
 *  - 映画切替時は前の映画のグラフ／状態を即時クリアし、古いレスポンスは破棄する
 */
import { ref, computed, watch, nextTick, onActivated, onBeforeUnmount } from 'vue';
import {
  Chart,
  CategoryScale,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
  Filler,
} from 'chart.js';
import type { Plugin } from 'chart.js';

import { getTrendsService } from '@/api/dp/index.js';
import { waitClientV2IndexChartsIdle } from '@/views/dp/client-v2/chart-nav-guard';

// 本コンポーネント単体でも描画できるよう必要な要素を登録（重複登録は無害）
Chart.register(CategoryScale, LinearScale, LineController, LineElement, PointElement, Tooltip, Filler);

const props = defineProps<{
  movieId: number | string;
  /** 本片の公開日（YYYY-MM-DD）。ウィンドウ内にあれば縦線で表示 */
  openDate?: string;
  /** 公開後モードの選択期間（YYYY-MM-DD）。ウィンドウと重なる部分の背景を強調 */
  periodStart?: string;
  periodEnd?: string;
}>();

interface TrendPoint {
  date: string;
  /** 0〜100 の相対値。Google が「データなし」とした日は null */
  value: number | null;
  /** 集計が完了していない直近の点 */
  partial: boolean;
}

type TrendsStatus = 'ok' | 'no_data' | 'failed' | 'not_covered';

interface TrendsPayload {
  status: TrendsStatus;
  keyword: string | null;
  geo: string | null;
  category: number | null;
  timeframeStart: string | null;
  timeframeEnd: string | null;
  crawledAt: string | null;
  points: TrendPoint[];
}

/** 画面状態：idle（映画未選択）/ loading / 後端 status / error（API 取得失敗） */
type ViewState = 'idle' | 'loading' | TrendsStatus | 'error';

const KNOWN_STATUSES: TrendsStatus[] = ['ok', 'no_data', 'failed', 'not_covered'];

/** グラフ以外の状態メッセージ（0 で埋めず、状態ごとに明示する） */
const STATE_MESSAGE: Record<Exclude<ViewState, 'idle' | 'ok'>, string> = {
  loading: '読み込み中…',
  no_data: 'Google トレンドに十分な検索データがありません',
  failed: 'Google トレンドのデータ取得に失敗しました（次回の更新で再取得します）',
  not_covered: 'この作品は Google トレンドの集計対象外です',
  error: 'データを取得できませんでした。時間をおいて再度お試しください',
};

const GEO_LABEL: Record<string, string> = { JP: '日本' };
const CATEGORY_LABEL: Record<number, string> = { 0: 'すべてのカテゴリ', 34: '映画' };

/** Google トレンドのブランドカラー（青） */
const TREND_COLOR = '#4285F4';

const state = ref<ViewState>('idle');
const payload = ref<TrendsPayload | null>(null);
const canvasRef = ref<HTMLCanvasElement | null>(null);
let chart: Chart<'line'> | null = null;
let fetchSeq = 0;

const geoLabel = computed(() => {
  const geo = payload.value?.geo;
  return geo ? GEO_LABEL[geo] ?? geo : '—';
});

const categoryLabel = computed(() => {
  const category = payload.value?.category;
  if (category === null || category === undefined) return '—';
  return CATEGORY_LABEL[category] ?? `ID ${category}`;
});

const labels = computed(() => payload.value?.points.map((p) => p.date) ?? []);

/** 公開日がウィンドウ内にあるか（凡例表示判定） */
const openDateInWindow = computed(() => !!props.openDate && labels.value.includes(props.openDate));

/** 選択期間とウィンドウの重なり [start, end]。重ならなければ null */
const periodOverlap = computed<[string, string] | null>(() => {
  const list = labels.value;
  if (!props.periodStart || !props.periodEnd || list.length === 0) return null;
  // YYYY-MM-DD は文字列比較で日付順になる
  const start = props.periodStart < list[0] ? list[0] : props.periodStart;
  const end = props.periodEnd > list[list.length - 1] ? list[list.length - 1] : props.periodEnd;
  return start <= end ? [start, end] : null;
});

const hasPartial = computed(() => !!payload.value?.points.some((p) => p.partial && p.value !== null));

/** データ不足（value=null）の連続区間 [開始index, 終了index]。グレー背景で明示し、0 としては描かない */
const noDataRuns = computed<[number, number][]>(() => {
  const runs: [number, number][] = [];
  const list = payload.value?.points ?? [];
  let start = -1;
  list.forEach((p, i) => {
    if (p.value === null) {
      if (start < 0) start = i;
    } else if (start >= 0) {
      runs.push([start, i - 1]);
      start = -1;
    }
  });
  if (start >= 0) runs.push([start, list.length - 1]);
  return runs;
});

/** YYYY-MM-DD → YYYY/MM/DD */
const formatDate = (date: string | null) => (date ? date.replace(/-/g, '/') : '—');

/** API レスポンス（裸 DTO）を正規化。想定外の形なら null */
const pickPayload = (res: any): TrendsPayload | null => {
  const body = res && typeof res.status === 'string' ? res : res?.data;
  if (!body || !KNOWN_STATUSES.includes(body.status)) return null;
  return {
    status: body.status,
    keyword: body.keyword ?? null,
    geo: body.geo ?? null,
    category: typeof body.category === 'number' ? body.category : null,
    timeframeStart: body.timeframeStart ?? null,
    timeframeEnd: body.timeframeEnd ?? null,
    crawledAt: body.crawledAt ?? null,
    points: Array.isArray(body.points)
      ? body.points.map((p: any) => ({
          date: String(p?.date ?? ''),
          // 数値以外（null / 欠落）は null のまま保持し、0 に変換しない
          value: typeof p?.value === 'number' ? p.value : null,
          partial: p?.partial === true,
        }))
      : [],
  };
};

const isDark = () => document.documentElement.classList.contains('dark');

/** データ不足日のグレー背景・選択期間の背景・公開日の縦線を描くインラインプラグイン（描画時に props を参照） */
const markerPlugin: Plugin<'line'> = {
  id: 'googleTrendsMarkers',
  beforeDatasetsDraw(c) {
    const { ctx, chartArea, scales } = c;
    const list = c.data.labels as string[];

    // データ不足の日：数値位置を持たない背景色で示す（1 日分の幅＝隣接点の間隔）
    const runs = noDataRuns.value;
    if (runs.length > 0 && list.length > 0) {
      const step = list.length > 1
        ? Math.abs(scales.x.getPixelForValue(1) - scales.x.getPixelForValue(0))
        : chartArea.right - chartArea.left;
      ctx.save();
      ctx.fillStyle = isDark() ? 'rgba(255, 255, 255, 0.07)' : 'rgba(0, 0, 0, 0.05)';
      runs.forEach(([runStart, runEnd]) => {
        const bandLeft = Math.max(chartArea.left, scales.x.getPixelForValue(runStart) - step / 2);
        const bandRight = Math.min(chartArea.right, scales.x.getPixelForValue(runEnd) + step / 2);
        if (bandRight > bandLeft) {
          ctx.fillRect(bandLeft, chartArea.top, bandRight - bandLeft, chartArea.bottom - chartArea.top);
        }
      });
      ctx.restore();
    }

    const overlap = periodOverlap.value;
    if (!overlap) return;
    const startIndex = list.indexOf(overlap[0]);
    const endIndex = list.indexOf(overlap[1]);
    if (startIndex < 0 || endIndex < 0) return;
    const left = scales.x.getPixelForValue(startIndex);
    const right = scales.x.getPixelForValue(endIndex);
    ctx.save();
    ctx.fillStyle = isDark() ? 'rgba(66, 133, 244, 0.16)' : 'rgba(66, 133, 244, 0.10)';
    // 1 日だけの期間でも見えるよう最小幅を確保
    ctx.fillRect(left, chartArea.top, Math.max(right - left, 3), chartArea.bottom - chartArea.top);
    ctx.restore();
  },
  afterDatasetsDraw(c) {
    if (!props.openDate) return;
    const index = (c.data.labels as string[]).indexOf(props.openDate);
    if (index < 0) return;
    const { ctx, chartArea, scales } = c;
    const x = scales.x.getPixelForValue(index);
    ctx.save();
    ctx.strokeStyle = '#EF4444';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([4, 3]);
    ctx.beginPath();
    ctx.moveTo(x, chartArea.top);
    ctx.lineTo(x, chartArea.bottom);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = '#EF4444';
    ctx.font = 'bold 11px sans-serif';
    // 右端に近い場合はラベルを線の左側へ
    const text = '公開日';
    const width = ctx.measureText(text).width;
    const textX = x + 4 + width > chartArea.right ? x - 4 - width : x + 4;
    ctx.fillText(text, textX, chartArea.top + 12);
    ctx.restore();
  },
};

/** グラフ破棄。親の一括破棄（keep-alive 離脱時）で既に破棄済みの場合もあるため例外は握りつぶす */
const destroyChart = () => {
  if (!chart) return;
  try {
    chart.stop();
    if (chart.ctx) chart.destroy();
  } catch {
    /* ignore */
  }
  chart = null;
};

const renderChart = () => {
  const canvas = canvasRef.value;
  const data = payload.value;
  if (!canvas || !data || state.value !== 'ok') return;
  destroyChart();
  // 同じ canvas に残った Chart があれば先に破棄（Chart.js は canvas 再利用時に例外を投げる）
  Chart.getChart(canvas)?.destroy();

  const points = data.points;
  // 前後がデータ不足（または端）の単日データ：線分を引けないため点で表示しないと見えなくなる
  const isolated = points.map((p, i) => p.value !== null
    && (i === 0 || points[i - 1].value === null)
    && (i === points.length - 1 || points[i + 1].value === null));
  const gridColor = isDark() ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)';
  const tickColor = isDark() ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.55)';

  chart = new Chart<'line'>(canvas, {
    type: 'line',
    data: {
      labels: points.map((p) => p.date),
      datasets: [
        {
          label: '検索インタレスト',
          data: points.map((p) => p.value),
          borderColor: TREND_COLOR,
          backgroundColor: 'rgba(66, 133, 244, 0.12)',
          fill: 'origin',
          borderWidth: 2,
          pointRadius: (ctx) => (isolated[ctx.dataIndex] ? 2.5 : 0),
          pointBackgroundColor: TREND_COLOR,
          pointHoverRadius: 5,
          tension: 0.3,
          // null 日（データなし）は線を繋がない
          spanGaps: false,
          // 集計中（partial）の点へ向かう区間は破線
          segment: {
            borderDash: (ctx) => (points[ctx.p1DataIndex]?.partial ? [6, 4] : undefined),
          },
        },
        // ツールチップ専用の補助系列：データ不足の日だけ値を持たせ、ホバー時に「データ不足」を表示する。
        // 線・点・塗りは一切描かないため、グラフ上で 0 として見えることはない
        {
          label: 'データ不足',
          data: points.map((p) => (p.value === null ? 0 : null)),
          showLine: false,
          pointRadius: 0,
          pointHoverRadius: 0,
          borderWidth: 0,
          fill: false,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      // 親ページの keep-alive 一括破棄と Animator の競合を避けるためアニメーションは無効
      animation: false,
      events: ['mousemove', 'mouseout', 'click', 'touchstart', 'touchmove'],
      interaction: { mode: 'index', intersect: false },
      plugins: {
        // ページ側で全体登録済みの Legend は使わず、テンプレートの凡例に統一（重複表示を防ぐ）
        legend: { display: false },
        tooltip: {
          displayColors: false,
          callbacks: {
            title: (items) => formatDate(items[0]?.label ?? null),
            label: (item) => {
              // 補助系列（index 1）はデータ不足の日にだけ値を持つ
              if (item.datasetIndex === 1) return 'データ不足（検索数が少ない日）';
              const point = points[item.dataIndex];
              return `検索インタレスト：${point?.value}${point?.partial ? '（集計中）' : ''}`;
            },
          },
        },
      },
      scales: {
        y: {
          min: 0,
          max: 100,
          ticks: { stepSize: 25, color: tickColor },
          grid: { color: gridColor },
        },
        x: {
          grid: { display: false },
          ticks: {
            color: tickColor,
            autoSkip: true,
            maxTicksLimit: 8,
            maxRotation: 0,
            // category 軸の value はインデックス。MM/DD 表記にする
            callback: (value) => points[Number(value)]?.date.slice(5).replace('-', '/') ?? '',
          },
        },
      },
    },
    plugins: [markerPlugin],
  });
};

const fetchTrends = () => {
  const seq = ++fetchSeq;
  // 映画切替時に前の映画のグラフ・状態が残らないよう即時クリア
  destroyChart();
  payload.value = null;
  if (!props.movieId) {
    state.value = 'idle';
    return;
  }
  state.value = 'loading';
  getTrendsService({ movieId: props.movieId })
    .then((res: any) => {
      // より新しいリクエストが発行済みなら破棄
      if (seq !== fetchSeq) return;
      const data = pickPayload(res);
      if (!data) {
        state.value = 'error';
        return;
      }
      payload.value = data;
      // ok でも描画できる点が 1 つも無ければ「データなし」として扱う
      state.value = data.status === 'ok' && !data.points.some((p) => p.value !== null) ? 'no_data' : data.status;
      if (state.value === 'ok') nextTick(renderChart);
    })
    .catch(() => {
      if (seq !== fetchSeq) return;
      state.value = 'error';
    });
};

watch(() => props.movieId, fetchTrends, { immediate: true });

// 公開日・選択期間の変更はプラグインの再描画だけで反映できる
watch(
  () => [props.openDate, props.periodStart, props.periodEnd],
  () => {
    if (chart) chart.update('none');
  },
);

// keep-alive 復帰：離脱時に親が canvas 配下の Chart を一括破棄しているため、破棄完了を待って再描画
onActivated(async () => {
  await waitClientV2IndexChartsIdle();
  const canvas = canvasRef.value;
  if (state.value !== 'ok' || !canvas || Chart.getChart(canvas)) return;
  chart = null;
  nextTick(renderChart);
});

onBeforeUnmount(() => {
  // 未完了リクエストの結果を破棄
  fetchSeq += 1;
  destroyChart();
});
</script>

<template>
  <div class="google-trends">
    <template v-if="state === 'ok' && payload">
      <div class="google-trends__meta">
        <span>期間：{{ formatDate(payload.timeframeStart) }}〜{{ formatDate(payload.timeframeEnd) }}</span>
        <span>地域：{{ geoLabel }}</span>
        <span>カテゴリ：{{ categoryLabel }}</span>
        <span v-if="payload.keyword">検索語：「{{ payload.keyword }}」</span>
      </div>
      <div class="google-trends__chart">
        <canvas ref="canvasRef"></canvas>
      </div>
      <div class="google-trends__legend">
        <span class="google-trends__legend-item">
          <i class="legend-mark legend-mark--line"></i>検索インタレスト
        </span>
        <span v-if="openDateInWindow" class="google-trends__legend-item">
          <i class="legend-mark legend-mark--open"></i>公開日
        </span>
        <span v-if="periodOverlap" class="google-trends__legend-item">
          <i class="legend-mark legend-mark--period"></i>選択期間
        </span>
        <span v-if="hasPartial" class="google-trends__legend-item">
          <i class="legend-mark legend-mark--partial"></i>集計中
        </span>
        <span v-if="noDataRuns.length > 0" class="google-trends__legend-item">
          <i class="legend-mark legend-mark--nodata"></i>データ不足（検索数が少ない日）
        </span>
      </div>
      <p class="google-trends__note">
        ※ 期間内の最高値を 100 とした相対値です。実際の検索回数ではありません。
        <template v-if="noDataRuns.length > 0">グレーの日は検索数が少なく Google トレンドにデータがない日で、0 ではありません。</template>
        <template v-if="payload.crawledAt">（データ更新：{{ payload.crawledAt.slice(0, 16) }}）</template>
      </p>
    </template>
    <div v-else-if="state !== 'idle'" class="google-trends__state">
      <p>{{ STATE_MESSAGE[state] }}</p>
      <p v-if="payload?.keyword" class="google-trends__state-sub">検索語：「{{ payload.keyword }}」</p>
    </div>
  </div>
</template>

<style lang="less" scoped>
.google-trends {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 200px;

  &__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 14px;
    font-size: 12px;
    color: var(--text-secondary);
  }

  &__chart {
    position: relative;
    height: 220px;

    @media (max-width: 768px) {
      height: 180px;
    }
  }

  &__legend {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 14px;
    font-size: 12px;
    color: var(--text-secondary);
  }

  &__legend-item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .legend-mark {
    display: inline-block;
    width: 16px;

    &--line {
      height: 0;
      border-top: 2px solid #4285f4;
    }

    &--open {
      height: 0;
      border-top: 2px dashed #ef4444;
    }

    &--period {
      height: 10px;
      background: rgba(66, 133, 244, 0.2);
    }

    &--partial {
      height: 0;
      border-top: 2px dashed #4285f4;
    }

    &--nodata {
      height: 10px;
      background: rgba(128, 128, 128, 0.22);
    }
  }

  &__note {
    margin: 0;
    font-size: 11px;
    color: var(--text-secondary);
  }

  &__state {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    text-align: center;
    color: var(--text-secondary);
    font-size: 13px;

    p {
      margin: 0;
    }
  }

  &__state-sub {
    font-size: 12px;
  }
}
</style>
